import { useCallback, useEffect, useRef, useState } from 'react'

import 'mapbox-gl/dist/mapbox-gl.css'
import MapboxMap, { type MapMouseEvent, type MapRef, NavigationControl } from 'react-map-gl/mapbox'

import { useSavedViews } from '@/components/ui/saved-views/useSavedViews'
import { useDashboard } from '@/hooks'
import { getCenterFromBounds } from '@/lib/map/geo'
import { mapStyleUrl } from '@/lib/map/MapStyleToggles'
import { addLabelBackgrounds, addTrackerMarkerImages, startRepaintLoop } from '@/lib/map/markers'

import { MapStyleToggles } from './MapStyleToggles'
import { TrackerDetailModal } from './TrackerDetailModal'
import { TrackerDetailSheet } from './TrackerDetailSheet'
import { TrackerHistorySlider } from './TrackerHistorySlider'
import { useTrackerMapContext, type TrackerSavedView } from './TrackerMapContext'
import { TrackerMapLayers } from './TrackerMapLayers'
import { TrackerMapLayerToggles } from './TrackerMapLayerToggles'
import { TrackerMapToolbar } from './TrackerMapToolbar'
import { TrackerPanel } from './TrackerPanel'
import { TrackerQuickView } from './TrackerQuickView'

const mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN

// Layers react-map-gl should treat as interactive so click/hover events carry evt.features.
const INTERACTIVE_LAYER_IDS = [
  'clustered-tracker-circle',
  'unclustered-tracker-marker',
  'current-tracker-marker',
  'history-points-circle',
]

export const TrackerMap: React.FC = () => {
  const context = useTrackerMapContext()
  const layoutMode = context.sidebarState.layoutMode
  const splitRatio = context.sidebarState.splitRatio
  const [mapReady, setMapReady] = useState(false)
  const [isSplitResizing, setIsSplitResizing] = useState(false)
  const [isMapPanelResizing, setIsMapPanelResizing] = useState(false)
  const splitContainerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<MapRef | null>(null)
  const currentIdRef = useRef<number | undefined>(undefined)

  const { isLoading: isDashboardLoading } = useDashboard()
  const { defaultView } = useSavedViews<TrackerSavedView>('trackers')

  // Once the profile is in, open the default saved view, unless this tab already has its own
  // state (coming back to the page keeps what the user left).
  useEffect(() => {
    if (isDashboardLoading || context.isReady) return
    if (defaultView && !context.hydrated.companyId) context.applyView(defaultView)
    context.setIsReady(true)
  }, [isDashboardLoading, context, defaultView])

  // Where the tracker table's header row and its last column sit inside the split container. The
  // detail sheet starts there, so its title bar reads as the table's next header cell.
  const [tableHeader, setTableHeader] = useState<{ top: number; height: number; left: number }>()
  const handleHeaderMeasured = useCallback((rect: { top: number; height: number; right: number }) => {
    const container = splitContainerRef.current
    if (!container) return
    const bounds = container.getBoundingClientRect()
    const next = { top: rect.top - bounds.top, height: rect.height, left: rect.right - bounds.left }
    setTableHeader((prev) =>
      prev?.top === next.top && prev.height === next.height && prev.left === next.left ? prev : next
    )
  }, [])

  // State rather than a ref, so the resize observer below follows the map's container.
  const [mapContainerEl, setMapContainerEl] = useState<HTMLDivElement | null>(null)
  useEffect(() => {
    const el = mapContainerEl
    if (!el) return
    let raf = 0
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => mapRef.current?.getMap()?.resize())
    })
    observer.observe(el)
    return () => {
      cancelAnimationFrame(raf)
      observer.disconnect()
    }
  }, [mapContainerEl])

  const selectTracker = useCallback(
    (id: number | undefined) => {
      const map = mapRef.current?.getMap()
      if (map && map.isStyleLoaded() && map.getSource('trackers-source')) {
        if (currentIdRef.current && currentIdRef.current !== id) {
          map.setFeatureState({ source: 'trackers-source', id: currentIdRef.current }, { selected: false })
        }
        if (id) map.setFeatureState({ source: 'trackers-source', id }, { selected: true })
      }
      currentIdRef.current = id
      context.setCurrentRowId(id)
    },
    [context]
  )

  // Clear map selection when the context selection is cleared (e.g. reset).
  useEffect(() => {
    if (context.currentRowId === undefined && currentIdRef.current !== undefined) {
      const map = mapRef.current?.getMap()
      if (map && map.isStyleLoaded() && map.getSource('trackers-source') && currentIdRef.current) {
        map.setFeatureState({ source: 'trackers-source', id: currentIdRef.current }, { selected: false })
      }
      currentIdRef.current = undefined
    }
  }, [context.currentRowId])

  const [cursor, setCursor] = useState('')

  // Single declarative click handler. react-map-gl populates evt.features for INTERACTIVE_LAYER_IDS,
  // so we don't need raw map.on('click', layer) handlers (those raced with the map-level deselect and
  // cleared the selection immediately). Cluster → zoom to its centroid; marker → select on the first
  // click and open the modal on the second; empty map → clear the selection.
  const onMapClick = useCallback(
    (evt: MapMouseEvent) => {
      const map = mapRef.current?.getMap()
      if (!map) return
      const feature = evt.features?.[0]

      // History mode: clicking a trail point opens its quick-view popup; empty
      // map dismisses it. The normal select/modal flow is suppressed here.
      if (context.historyMode) {
        if (feature?.layer?.id === 'history-points-circle') {
          const index = Number(feature.properties?.index)
          context.setHistorySelectedIndex(Number.isFinite(index) ? index : undefined)
        } else {
          context.setHistorySelectedIndex(undefined)
        }
        return
      }

      if (!feature) {
        selectTracker(undefined)
        return
      }
      if (feature.layer?.id === 'clustered-tracker-circle') {
        const clusterId = feature.properties?.cluster_id
        if (clusterId == null) return
        // Zoom toward the cluster's centroid, not the click point, so repeated drill-downs don't drift.
        const [lng, lat] = (feature.geometry as unknown as { coordinates: [number, number] }).coordinates
        selectTracker(undefined)
        const source = map.getSource('trackers-source') as mapboxgl.GeoJSONSource
        source.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err || zoom == null) return
          map.easeTo({ center: [lng, lat], zoom })
        })
        return
      }
      const id = Number(feature.properties?.id)
      if (!id) return
      if (id === currentIdRef.current) {
        // Second click on the already-selected row → open its details.
        context.openDetails()
      } else {
        // First click → just select it (the list scrolls it into view), matching the asset map.
        selectTracker(id)
      }
    },
    [context, selectTracker]
  )

  // Split (vertical) + map-panel (horizontal) drag-resizers.
  useEffect(() => {
    if (!isSplitResizing) return
    const onMove = (e: MouseEvent) => {
      const container = splitContainerRef.current
      if (!container) return
      const rect = container.getBoundingClientRect()
      const ratio = Math.max(0.2, Math.min(0.8, (e.clientY - rect.top) / rect.height))
      context.setSidebarState((prev) => ({ ...prev, splitRatio: ratio }))
    }
    const onUp = () => setIsSplitResizing(false)
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
  }, [isSplitResizing, context])

  useEffect(() => {
    if (!isMapPanelResizing) return
    const onMove = (e: MouseEvent) => {
      const container = splitContainerRef.current
      if (!container) return
      const rect = container.getBoundingClientRect()
      const width = Math.max(200, Math.min(rect.width - 200, e.clientX - rect.left))
      context.setListPanelWidth(width)
    }
    const onUp = () => {
      setIsMapPanelResizing(false)
      setTimeout(() => mapRef.current?.getMap()?.resize(), 50)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
  }, [isMapPanelResizing, context])

  useEffect(() => {
    setTimeout(() => mapRef.current?.getMap()?.resize(), 50)
  }, [layoutMode, splitRatio, context.sidebarState.listPanelWidth, context.detailSheetIsOpen])

  const sheetIsOpen = context.detailSheetIsOpen && !!context.currentRow
  const showSkeleton = context.isLoading && context.allTrackers.length === 0 && context.companyId

  return (
    <div className="flex flex-col min-w-0 overflow-x-hidden h-full w-full relative flex-1">
      <TrackerMapToolbar mapRef={mapRef} />
      <div className="flex flex-1 min-h-0">
        <div
          ref={splitContainerRef}
          className={`relative flex-1 min-w-0 min-h-0 flex ${layoutMode === 'split' ? 'flex-col' : 'flex-row'}`}
        >
          {layoutMode !== 'split' && <TrackerDetailSheet variant="overlay" tableHeader={tableHeader} />}

          {layoutMode === 'map' && (
            <>
              <div
                className="min-w-0 min-h-0 flex flex-col bg-base-100 h-full shrink-0"
                style={{ width: `${context.sidebarState.listPanelWidth}px` }}
              >
                <TrackerPanel mapRef={mapRef} setCurrentRowId={selectTracker} onHeaderMeasured={handleHeaderMeasured} />
              </div>
              <div
                onMouseDown={(e) => {
                  e.preventDefault()
                  setIsMapPanelResizing(true)
                }}
                className={`w-1.5 h-full cursor-col-resize transition-colors shrink-0 ${
                  isMapPanelResizing ? 'bg-primary' : 'bg-base-300 hover:bg-primary/60'
                }`}
                title="Drag to resize"
              />
            </>
          )}

          {/* Split layout: the sheet takes the map's half, and the map waits, mounted, underneath. */}
          {layoutMode === 'split' && sheetIsOpen && (
            <div className="min-w-0 min-h-0 w-full" style={{ flex: `${splitRatio} 1 0` }}>
              <TrackerDetailSheet variant="pane" />
            </div>
          )}

          <div
            ref={setMapContainerEl}
            className={`relative min-w-0 min-h-0 ${
              layoutMode === 'table' || (layoutMode === 'split' && sheetIsOpen)
                ? 'hidden'
                : layoutMode === 'split'
                  ? 'w-full'
                  : 'flex-1 h-full'
            }`}
            style={layoutMode === 'split' ? { flex: `${splitRatio} 1 0` } : undefined}
          >
            <div className="h-full flex flex-col">
              <div className="flex-1 min-h-0 relative rounded-lg overflow-hidden">
                <TrackerMapLayerToggles mapRef={mapRef} />
                <MapStyleToggles mapRef={mapRef} onChangeStyle={() => setMapReady(false)} />
                <div className="hidden md:block">
                  <TrackerQuickView />
                </div>
                <TrackerHistorySlider />
                {showSkeleton && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                    <div className="bg-base-100/95 rounded-lg shadow-lg px-4 py-3 flex items-center gap-3">
                      <span className="loading loading-spinner loading-md text-primary" />
                      <span className="text-sm">Loading trackers…</span>
                    </div>
                  </div>
                )}
                <MapboxMap
                  key={`${context.mapState.style}-${context.resetCount}`}
                  ref={mapRef}
                  mapboxAccessToken={mapboxToken}
                  initialViewState={{
                    ...getCenterFromBounds(context.mapState.bounds),
                    zoom: context.mapState.zoom,
                    pitch: context.mapState.style === '3d' ? context.mapState.pitch : 0,
                    bearing: context.mapState.style === '3d' ? context.mapState.bearing : 0,
                  }}
                  onLoad={() => {
                    const map = mapRef.current?.getMap()
                    if (!map) return
                    const bounds = map.getBounds()
                    if (bounds) {
                      context.setMapState((prev) => ({
                        ...prev,
                        bounds: {
                          north: bounds.getNorth(),
                          south: bounds.getSouth(),
                          east: bounds.getEast(),
                          west: bounds.getWest(),
                        },
                      }))
                    }
                    addTrackerMarkerImages(map)
                    addLabelBackgrounds(map)
                    startRepaintLoop(map)
                    setMapReady(true)
                  }}
                  style={{ width: '100%', height: '100%' }}
                  mapStyle={mapStyleUrl(context.mapState.style)}
                  onMove={(evt) => {
                    const map = mapRef.current?.getMap()
                    let bounds = context.mapState.bounds
                    const b = map?.getBounds()
                    if (b) {
                      bounds = { north: b.getNorth(), south: b.getSouth(), east: b.getEast(), west: b.getWest() }
                    }
                    context.setMapState((prev) => ({ ...prev, zoom: evt.viewState.zoom || 6, bounds }))
                  }}
                  interactiveLayerIds={INTERACTIVE_LAYER_IDS}
                  cursor={cursor}
                  onMouseEnter={() => setCursor('pointer')}
                  onMouseLeave={() => setCursor('')}
                  onClick={onMapClick}
                >
                  <NavigationControl
                    position="bottom-left"
                    style={{ display: window.innerWidth < 768 ? 'none' : 'block' }}
                  />
                  {mapReady && <TrackerMapLayers />}
                </MapboxMap>
              </div>
            </div>
          </div>

          {layoutMode === 'split' && (
            <div
              onMouseDown={(e) => {
                e.preventDefault()
                setIsSplitResizing(true)
              }}
              className={`h-1.5 w-full cursor-row-resize transition-colors shrink-0 ${
                isSplitResizing ? 'bg-primary' : 'bg-base-300 hover:bg-primary/60'
              }`}
              title="Drag to resize"
            />
          )}

          {layoutMode !== 'map' && (
            <div
              className={`min-w-0 min-h-0 flex flex-col bg-base-100 ${
                layoutMode === 'split' ? 'w-full' : 'flex-1 h-full'
              }`}
              style={layoutMode === 'split' ? { flex: `${1 - splitRatio} 1 0` } : undefined}
            >
              <TrackerPanel mapRef={mapRef} setCurrentRowId={selectTracker} onHeaderMeasured={handleHeaderMeasured} />
            </div>
          )}
        </div>
      </div>

      <TrackerDetailModal />
    </div>
  )
}
